// GPT-Live の WebRTC の接続 (spec 2026-09-14 L3 / §3.4)。
//
// GPT-Live にはエフェメラルキーが無いので、ブラウザは WebRTC でしか繋げない。offer を作って
// こちらの Pages Function (/api/live/session) に渡し、サーバが上流にセッションを作って answer を返す。
// 音声はメディアトラック、イベントは data channel "oai-events" に乗る。受信音声は <audio> で鳴らす
// (liveAudioOutput.ts が 1 つだけ持ち、事前チェックと本編の 2 セッションで使い回す)。
//
// イベントの読み書きは liveClient.ts が持つ。ここは「繋ぐ・送る・受ける・閉じる」と受信音量だけ。

const SESSION_ENDPOINT = "/api/live/session";
const DATA_CHANNEL = "oai-events";
/** answer を適用してから data channel が開くまでの上限 */
export const OPEN_TIMEOUT_MS = 10_000;

export type LiveTransportOpts = {
  purpose: "preflight" | "survey";
  questions?: { id: string; text: string }[];
  stream: MediaStream;
  audio: HTMLAudioElement | null;
  onMessage: (raw: string) => void;
  /** 相手から切れた (こちらが close したときは呼ばない) */
  onClose: () => void;
};

type StatLike = { type?: string; kind?: string; audioLevel?: unknown };

export class LiveTransport {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private closed = false;
  sessionId: string | null = null;

  constructor(private opts: LiveTransportOpts) {}

  /** 繋ぐ。失敗したら throw する (呼び出し側が onError に倒す) */
  async connect(): Promise<void> {
    const Ctor = (globalThis as { RTCPeerConnection?: typeof RTCPeerConnection }).RTCPeerConnection;
    if (!Ctor) throw new Error("この環境は WebRTC に対応していません");
    const pc = new Ctor();
    this.pc = pc;
    pc.ontrack = (e) => {
      const el = this.opts.audio;
      const remote = e.streams[0];
      if (!el || !remote) return;
      el.srcObject = remote;
      try {
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => undefined);
      } catch {
        // 鳴らせなくても会話は続ける (原則 5)
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") this.handleClose();
    };
    for (const track of this.opts.stream.getAudioTracks()) pc.addTrack(track, this.opts.stream);
    const dc = pc.createDataChannel(DATA_CHANNEL);
    this.dc = dc;
    dc.onmessage = (e) => this.opts.onMessage(String(e.data));
    dc.onclose = () => this.handleClose();

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const res = await fetch(SESSION_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        purpose: this.opts.purpose,
        sdp: offer.sdp,
        ...(this.opts.questions ? { questions: this.opts.questions } : {}),
      }),
    });
    if (!res.ok) throw new Error(`session の作成に失敗: ${res.status}`);
    const body = (await res.json()) as { sessionId?: unknown; sdp?: unknown };
    if (typeof body.sdp !== "string" || !body.sdp) throw new Error("answer が返らなかった");
    this.sessionId = typeof body.sessionId === "string" ? body.sessionId : null;
    await pc.setRemoteDescription({ type: "answer", sdp: body.sdp });
    // **開いてから命令を送る** (SDK の注記: session.started を data channel で待ってから)
    if (dc.readyState !== "open") {
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error("data channel が開かない")), OPEN_TIMEOUT_MS);
        dc.addEventListener(
          "open",
          () => {
            clearTimeout(t);
            resolve();
          },
          { once: true }
        );
      });
    }
  }

  /**
   * 開いているときだけ送る。送れなくても呼び出し元は止めない。
   * **readyState の判定と try/catch は二重の網** (変異検証で片方だけ外しても観測差が出ないことを
   * 確かめた、2026-09-14。2.1 の realtimeClient と同じ) — 実ブラウザは開いていない channel への
   * send を InvalidStateError で弾くので、判定を外しても catch が受ける
   */
  send(payload: Record<string, unknown>): void {
    const dc = this.dc;
    if (!dc || dc.readyState !== "open") return;
    try {
      dc.send(JSON.stringify(payload));
    } catch {
      // 閉じかけの channel などで送れないことがある
    }
  }

  /**
   * 受信音声の大きさ (0..1)。**AI が話し終えたかの主な手がかり** (spec L11 — 出力の文字起こしは
   * 音声より 0.3〜2.8 秒先に届くので、文字起こしだけでは判定できない)。取れなければ null。
   */
  async remoteLevel(): Promise<number | null> {
    const pc = this.pc;
    if (!pc || typeof pc.getStats !== "function") return null;
    try {
      const stats = await pc.getStats();
      let level = null as number | null;
      stats.forEach((s: StatLike) => {
        if (s.type === "inbound-rtp" && s.kind === "audio" && typeof s.audioLevel === "number") {
          level = s.audioLevel;
        }
      });
      return level;
    } catch {
      return null;
    }
  }

  /** 送信を止める / 戻す。**マイク自体は止めない** (R2 用の録音が同じトラックを使っている) */
  setSending(enabled: boolean): void {
    const pc = this.pc;
    if (!pc) return;
    const track = this.opts.stream.getAudioTracks()[0] ?? null;
    for (const s of pc.getSenders()) {
      void s.replaceTrack(enabled ? track : null).catch(() => undefined);
    }
  }

  /** 閉じる。**<audio> は捨てない** (iOS の再生の許可を保つ。流していたストリームだけ外す) */
  close(): void {
    this.closed = true;
    try {
      this.dc?.close();
    } catch {
      // 既に閉じている
    }
    try {
      this.pc?.close();
    } catch {
      // 既に閉じている
    }
    this.dc = null;
    this.pc = null;
    const el = this.opts.audio;
    if (el) {
      try {
        el.srcObject = null;
      } catch {
        // 外せなくても次の接続で上書きされる
      }
    }
  }

  private handleClose(): void {
    if (this.closed) return;
    this.closed = true;
    this.opts.onClose();
  }
}
